import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  money,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  FinancialStatementStyles,
  PresetChips,
  printStatement,
} from "./FinancialStatementShared";
import { SbStyles, csvCell, escapeHtml } from "./SupplierBalanceReport";

const TITLE = "Subcategory Ledgers";

export const TYPES = {
  hr: { label: "Employees", icon: "fa-id-badge" },
  supplier: { label: "Suppliers", icon: "fa-truck" },
  customer: { label: "Customers", icon: "fa-user" },
  distributor: { label: "Distributors", icon: "fa-sitemap" },
  assets: { label: "Assets", icon: "fa-cubes" },
  expense: { label: "Expenses", icon: "fa-money" },
  new: { label: "Custom", icon: "fa-tag" },
};

export const typeLabel = (type) => TYPES[type]?.label || type;

// Built-in sub-ledgers are named by their type; custom ones by their own name.
const subLedgerName = (sub) =>
  sub.type === "new" ? sub.name : typeLabel(sub.type);

const SlStyles = () => (
  <style>
    {`
      .sl-group > td {
        background: #f4f5fa;
        border-top: 1px solid #e6e8f0;
        font-weight: 600;
        color: #2a2962;
      }
      .sl-group-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 0;
        border: 0;
        background: none;
        color: inherit;
        font-weight: 700;
        text-align: left;
        cursor: pointer;
      }
      .sl-group-btn:hover { color: #e42320; }
      .sl-parent { display: block; margin-left: 22px; font-size: 0.7rem; font-weight: 400; color: #8b90a5; }
      .sl-row > td:nth-child(2) { padding-left: 34px; }
      .sl-type {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 2px 9px;
        border-radius: 10px;
        background: #eef0f8;
        color: #2a2962;
        font-size: 0.7rem;
        font-weight: 600;
        white-space: nowrap;
      }
      .sl-link { font-weight: 600; color: #2a2962; }
      .sl-link:hover { color: #e42320; text-decoration: underline; }
      .sl-link i { margin-left: 6px; font-size: 0.7rem; color: #b6bacb; }
      .sl-unused { color: #b6bacb; font-size: 0.75rem; }
    `}
  </style>
);

const SubcategoryListReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [collapsed, setCollapsed] = useState({});

  const fetchData = async () => {
    setLoading(true);
    setAlert(null);
    try {
      const res = await Http.get("/sub-category-list");
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the sub-ledgers. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const allSubs = useMemo(
    () => (data ? data.accounts.flatMap((account) => account.sub_ledgers) : []),
    [data],
  );

  const typeCounts = useMemo(
    () =>
      allSubs.reduce(
        (acc, sub) => ({ ...acc, [sub.type]: (acc[sub.type] || 0) + 1 }),
        {},
      ),
    [allSubs],
  );

  // Accounts with their matching sub-ledgers; an account whose own code or
  // name matches the search keeps all of them.
  const groups = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (data ? data.accounts : [])
      .map((account) => {
        const accountHit =
          query &&
          `${account.code} ${account.name}`.toLowerCase().includes(query);
        const subs = account.sub_ledgers.filter(
          (sub) =>
            (type === "all" || sub.type === type) &&
            (status === "all" || sub.status === status) &&
            (!query ||
              accountHit ||
              `${subLedgerName(sub)} ${sub.code || ""} ${sub.remarks || ""}`
                .toLowerCase()
                .includes(query)),
        );
        return { ...account, subs };
      })
      .filter((account) => account.subs.length > 0);
  }, [data, search, type, status]);

  const visibleSubs = groups.flatMap((group) => group.subs);
  const totals = {
    entries: visibleSubs.reduce((sum, sub) => sum + sub.entries, 0),
    debit: visibleSubs.reduce((sum, sub) => sum + num(sub.debit), 0),
    credit: visibleSubs.reduce((sum, sub) => sum + num(sub.credit), 0),
    opening: visibleSubs.reduce(
      (sum, sub) => sum + num(sub.opening_balance),
      0,
    ),
  };

  const activeCount = allSubs.filter((sub) => sub.status === "active").length;
  const unused = allSubs.filter((sub) => !sub.entries).length;
  const allEntries = allSubs.reduce((sum, sub) => sum + sub.entries, 0);

  const allExpanded = groups.every((group) => !collapsed[group.id]);
  const toggleAll = () =>
    setCollapsed(
      allExpanded
        ? groups.reduce((acc, group) => ({ ...acc, [group.id]: true }), {})
        : {},
    );
  const toggle = (id) =>
    setCollapsed((values) => ({ ...values, [id]: !values[id] }));

  const filterNote = [
    type !== "all" && typeLabel(type),
    status !== "all" && (status === "active" ? "Active" : "Inactive"),
    search.trim() && `matching "${search.trim()}"`,
  ]
    .filter(Boolean)
    .join(" · ");

  const exportCsv = () => {
    const lines = [
      [TITLE],
      filterNote ? [filterNote] : [],
      [],
      [
        "Account Code",
        "Account",
        "Type",
        "Sub-ledger",
        "Code",
        "Effective",
        "Status",
        "Opening",
        "Entries",
        "Debit",
        "Credit",
        "Last Entry",
      ],
      ...groups.flatMap((group) =>
        group.subs.map((sub) => [
          group.code,
          group.name,
          typeLabel(sub.type),
          subLedgerName(sub),
          sub.code,
          sub.effective_date,
          sub.status,
          num(sub.opening_balance).toFixed(2),
          sub.entries,
          num(sub.debit).toFixed(2),
          num(sub.credit).toFixed(2),
          sub.last_date,
        ]),
      ),
      [],
      [
        "",
        "Total",
        "",
        "",
        "",
        "",
        "",
        totals.opening.toFixed(2),
        totals.entries,
        totals.debit.toFixed(2),
        totals.credit.toFixed(2),
      ],
    ];
    const csv = lines.map((line) => line.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "subcategory-ledgers.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const headHtml = `
      <th>Sub-ledger</th><th>Code</th><th>Status</th>
      <th class="num">Opening</th><th class="num">Entries</th>
      <th class="num">Debit</th><th class="num">Credit</th><th>Last Entry</th>`;
    const bodyHtml =
      groups
        .map(
          (group) => `
        <tr class="group">
          <td colspan="4">${escapeHtml(group.code)} - ${escapeHtml(group.name)}</td>
          <td class="num">${group.subs.reduce((s, sub) => s + sub.entries, 0)}</td>
          <td class="num">${money(group.subs.reduce((s, sub) => s + num(sub.debit), 0))}</td>
          <td class="num">${money(group.subs.reduce((s, sub) => s + num(sub.credit), 0))}</td>
          <td></td>
        </tr>
        ${group.subs
          .map(
            (sub) => `
          <tr class="account">
            <td style="padding-left:24px">${escapeHtml(subLedgerName(sub))}</td>
            <td class="code">${escapeHtml(sub.code)}</td>
            <td>${sub.status === "active" ? "Active" : "Inactive"}</td>
            <td class="num">${money(sub.opening_balance)}</td>
            <td class="num">${sub.entries}</td>
            <td class="num">${money(sub.debit)}</td>
            <td class="num">${money(sub.credit)}</td>
            <td>${sub.last_date ? formatDate(sub.last_date) : ""}</td>
          </tr>`,
          )
          .join("")}`,
        )
        .join("") +
      `<tr class="grand">
        <td colspan="3">Total</td>
        <td class="num">${money(totals.opening)}</td>
        <td class="num">${totals.entries}</td>
        <td class="num">${money(totals.debit)}</td>
        <td class="num">${money(totals.credit)}</td>
        <td></td>
      </tr>`;

    printStatement({
      title: TITLE,
      subtitle: `${filterNote || "All sub-ledgers"} &middot; Postings to date &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
    });
  };

  const stat = (label, icon, value, sub, className = "") => (
    <div className="col-md-3 mg-b-10">
      <div className={`coa-stat ${className}`}>
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{value}</span>
        <span className="coa-stat-sub">{sub}</span>
      </div>
    </div>
  );

  const typePresets = [
    ["All types", "all"],
    ...Object.keys(TYPES)
      .filter((key) => typeCounts[key])
      .map((key) => [`${TYPES[key].label} (${typeCounts[key]})`, key]),
  ];

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <SbStyles />
        <SlStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">{TITLE}</h5>
            <p className="text-muted mb-0 tx-12">
              Subsidiary ledgers by chart of account, with postings to date
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={fetchData}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={exportCsv}
              disabled={loading || !groups.length}
            >
              <i className="fa fa-file-excel-o mg-r-6"></i>Export CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !groups.length}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          {data && (
            <div className="row mg-b-20">
              {stat(
                "Accounts",
                "fa-book",
                data.accounts.length,
                "Chart of accounts with sub-ledgers",
              )}
              {stat(
                "Sub-ledgers",
                "fa-folder-open-o",
                allSubs.length,
                `${activeCount} active · ${allSubs.length - activeCount} inactive`,
              )}
              {stat(
                "Postings",
                "fa-exchange",
                allEntries.toLocaleString(),
                "Journal lines tagged to a sub-ledger",
              )}
              {stat(
                "Unused",
                "fa-circle-o",
                unused,
                unused
                  ? "Sub-ledgers with no postings yet"
                  : "Every sub-ledger is in use",
                unused ? "" : "is-ok",
              )}
            </div>
          )}

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-filter me-2"></i>Filters
              </span>
              {data && (
                <span className="coa-head-note">
                  Showing {visibleSubs.length} of {allSubs.length} sub-ledgers
                </span>
              )}
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-4">
                  <label>Search</label>
                  <div className="sb-search">
                    <i className="fa fa-search" aria-hidden="true"></i>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Account, sub-ledger or code"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label="Search"
                    />
                  </div>
                </div>
                <div className="col-lg-5 mg-t-10 mg-lg-t-0">
                  <label>Type</label>
                  <PresetChips
                    presets={typePresets}
                    isActive={([, value]) => type === value}
                    onPick={([, value]) => setType(value)}
                  />
                </div>
                <div className="col-lg-3 mg-t-10 mg-lg-t-0">
                  <label>Status</label>
                  <PresetChips
                    presets={[
                      ["All", "all"],
                      ["Active", "active"],
                      ["Inactive", "inactive"],
                    ]}
                    isActive={([, value]) => status === value}
                    onPick={([, value]) => setStatus(value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>Sub-ledgers by Account
                <span className="coa-head-note mg-l-10">
                  Open a sub-ledger to see its ledger
                </span>
              </span>
              {groups.length > 0 && (
                <span className="coa-no-print">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={toggleAll}
                  >
                    <i
                      className={`fa fa-${allExpanded ? "compress" : "expand"} mg-r-6`}
                    ></i>
                    {allExpanded ? "Collapse all" : "Expand all"}
                  </button>
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The sub-ledgers could not be loaded.
              </div>
            ) : !groups.length ? (
              <div className="coa-empty">
                <i className="fa fa-folder-open-o"></i>
                {allSubs.length
                  ? "No sub-ledgers match the filters."
                  : "No sub-ledgers have been set up yet."}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="sb-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Sub-ledger</th>
                      <th>Code</th>
                      <th>Effective</th>
                      <th>Status</th>
                      <th className="coa-num">Opening</th>
                      <th className="coa-num">Entries</th>
                      <th className="coa-num">Debit</th>
                      <th className="coa-num">Credit</th>
                      <th>Last Entry</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {groups.map((group) => {
                      const open = !collapsed[group.id];
                      return (
                        <React.Fragment key={group.id}>
                          <tr className="sl-group">
                            <td colSpan={6}>
                              <button
                                type="button"
                                className="sl-group-btn"
                                onClick={() => toggle(group.id)}
                                aria-expanded={open}
                              >
                                <i
                                  className={`fa fa-caret-${open ? "down" : "right"}`}
                                  aria-hidden="true"
                                ></i>
                                <span className="coa-code">{group.code}</span>
                                {group.name}
                                <span
                                  className="coa-chip-count"
                                  title="Sub-ledgers shown"
                                >
                                  {group.subs.length}
                                </span>
                              </button>
                              {group.parent_name && (
                                <span className="sl-parent">
                                  Under {group.parent_code} -{" "}
                                  {group.parent_name}
                                </span>
                              )}
                            </td>
                            <td className="coa-num">
                              {group.subs.reduce(
                                (s, sub) => s + sub.entries,
                                0,
                              )}
                            </td>
                            <td className="coa-num">
                              {money(
                                group.subs.reduce(
                                  (s, sub) => s + num(sub.debit),
                                  0,
                                ),
                              )}
                            </td>
                            <td className="coa-num">
                              {money(
                                group.subs.reduce(
                                  (s, sub) => s + num(sub.credit),
                                  0,
                                ),
                              )}
                            </td>
                            <td>
                              <Link
                                to={`/chart-of-account-ledger/${group.id}`}
                                className="sl-link coa-no-print"
                                title="Open the account ledger"
                                style={{ fontSize: "0.75rem" }}
                              >
                                Account ledger
                                <i className="fa fa-external-link"></i>
                              </Link>
                            </td>
                          </tr>
                          {open &&
                            group.subs.map((sub) => (
                              <tr className="sb-row sl-row" key={sub.id}>
                                <td>
                                  <span className="sl-type">
                                    <i
                                      className={`fa ${TYPES[sub.type]?.icon || "fa-tag"}`}
                                      aria-hidden="true"
                                    ></i>
                                    {typeLabel(sub.type)}
                                  </span>
                                </td>
                                <td>
                                  <Link
                                    to={`/subcategory-ledger-report/${sub.id}`}
                                    className="sl-link"
                                    title="Open the sub-ledger"
                                  >
                                    {subLedgerName(sub)}
                                    <i className="fa fa-angle-right"></i>
                                  </Link>
                                  {sub.remarks && (
                                    <span className="sb-sub">
                                      {sub.remarks}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {sub.code ? (
                                    <span className="coa-code">{sub.code}</span>
                                  ) : (
                                    <span className="sl-unused">—</span>
                                  )}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  {sub.effective_date ? (
                                    formatDate(sub.effective_date)
                                  ) : (
                                    <span className="sl-unused">—</span>
                                  )}
                                </td>
                                <td>
                                  <span
                                    className={`coa-pill ${
                                      sub.status === "active"
                                        ? "coa-pill-ok"
                                        : "coa-pill-muted"
                                    }`}
                                  >
                                    {sub.status === "active"
                                      ? "Active"
                                      : "Inactive"}
                                  </span>
                                </td>
                                <td className="coa-num">
                                  {num(sub.opening_balance) ? (
                                    money(sub.opening_balance)
                                  ) : (
                                    <span className="sl-unused">—</span>
                                  )}
                                </td>
                                <td className="coa-num">
                                  {sub.entries || (
                                    <span className="sl-unused">Unused</span>
                                  )}
                                </td>
                                <td className="coa-num">
                                  {num(sub.debit) ? money(sub.debit) : ""}
                                </td>
                                <td className="coa-num">
                                  {num(sub.credit) ? money(sub.credit) : ""}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  {sub.last_date
                                    ? formatDate(sub.last_date)
                                    : ""}
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} style={{ textAlign: "right" }}>
                        Total · {visibleSubs.length} sub-ledger
                        {visibleSubs.length === 1 ? "" : "s"}
                      </td>
                      <td className="coa-num">{money(totals.opening)}</td>
                      <td className="coa-num">{totals.entries}</td>
                      <td className="coa-num">{money(totals.debit)}</td>
                      <td className="coa-num">{money(totals.credit)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubcategoryListReport;
